import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ShieldCheck, CircleDashed } from 'lucide-react';
import { Input } from '@/shared/ui/Input';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { formatEur } from '@/lib/utils';
import { useExtraServices } from './extra-services.api';
import { ExtraServiceReviewDialog } from './ExtraServiceReviewDialog';

export function ExtraServicesPage() {
  const [q, setQ] = useState('');
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'confirmed' ? 'confirmed' : 'open';
  const [tab, setTab] = useState<'open' | 'confirmed'>(initialTab);
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data, isLoading } = useExtraServices({ q, confirmedInternal: tab === 'confirmed' });

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-slate-900">Zusatzleistungen</h1>

      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setTab('open')}
          className={`inline-flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded-lg border text-sm font-medium sm:flex-none sm:px-5 ${
            tab === 'open'
              ? 'border-brand-700 bg-brand-700 text-white'
              : 'border-slate-300 bg-white text-slate-700'
          }`}
        >
          <CircleDashed className="h-4 w-4" /> Offen
        </button>
        <button
          onClick={() => setTab('confirmed')}
          className={`inline-flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded-lg border text-sm font-medium sm:flex-none sm:px-5 ${
            tab === 'confirmed'
              ? 'border-brand-700 bg-brand-700 text-white'
              : 'border-slate-300 bg-white text-slate-700'
          }`}
        >
          <ShieldCheck className="h-4 w-4" /> Bestätigt
        </button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title={tab === 'open' ? 'Keine offenen Zusatzleistungen' : 'Noch nichts bestätigt'}
          hint={
            tab === 'open'
              ? 'Meldungen aus dem Außendienst mit Zusatzleistungen erscheinen hier.'
              : 'Bestätigte Positionen sind bereit für die Kundenfreigabe.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {data.items.map((e) => (
            <li
              key={e.id}
              onClick={() => setDetailId(e.id)}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-medium text-slate-900">{e.title}</p>
                <span className="shrink-0 text-sm font-semibold text-slate-900">
                  {formatEur(e.netAmount)}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                {e.fieldReport && (
                  <>
                    <span>{e.fieldReport.project.customer.companyName}</span>
                    <span>{e.fieldReport.project.title}</span>
                  </>
                )}
                <span>{e.detectedBy === 'EMPLOYEE' ? 'vom Mitarbeiter' : e.detectedBy}</span>
              </div>
              {e.reason && <p className="mt-1 truncate text-sm text-slate-600">{e.reason}</p>}
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Positionen</p>}

      <ExtraServiceReviewDialog serviceId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
